import React, { useMemo, useState } from 'react';
import { CalendarClock, CheckCircle2, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import {
  boutonPrincipal, boutonSecondaire, Carte, champ, Erreur, fmtDate, PRIORITES, Pastille,
  STATUTS_ACTION, TexteLignes, Vide,
} from './ui';
import { annoncerErreur, annoncerSucces, confirmer } from '../../etats';

const VIDE = { description: '', sous_domaine_nom: '', priorite: 'important', date_debut: '', echeance: '', responsable: '', indicateurs: '', note_suivi: '' };

function FormulaireAction({ initial, diagnostics, onAnnuler, onEnregistrer }) {
  const [valeurs, setValeurs] = useState(() => ({ ...VIDE, diagnostic: diagnostics[diagnostics.length - 1]?.id || '', ...initial }));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const maj = (cle) => (e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }));

  const soumettre = async (e) => {
    e.preventDefault();
    if (!valeurs.description.trim()) { setErreur("Décrivez l'action."); return; }
    if (valeurs.date_debut && valeurs.echeance && valeurs.echeance < valeurs.date_debut) {
      setErreur("L'échéance précède la date de début."); return;
    }
    setEnvoi(true);
    setErreur('');
    try {
      const donnees = { ...valeurs, date_debut: valeurs.date_debut || null, echeance: valeurs.echeance || null };
      await onEnregistrer(donnees);
      annoncerSucces(initial?.id ? 'Action modifiée.' : 'Action ajoutée au plan.');
    } catch (err) {
      setErreur(err.message);
      setEnvoi(false);
    }
  };

  return (
    <form onSubmit={soumettre} className="bg-[var(--bg-main)] rounded-pastille p-4 space-y-3">
      {!initial?.id && diagnostics.length > 1 && (
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Diagnostic
          <select value={valeurs.diagnostic} onChange={maj('diagnostic')} className={`${champ} mt-1`}>
            {diagnostics.map((d) => <option key={d.id} value={d.id}>Diagnostic {d.type_diagnostic}</option>)}
          </select>
        </label>
      )}
      <label className="block text-xs font-semibold text-[var(--text-muted)]">Action *
        <textarea value={valeurs.description} onChange={maj('description')} rows={3} className={`${champ} mt-1 resize-y`} />
      </label>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Priorité
          <select value={valeurs.priorite} onChange={maj('priorite')} className={`${champ} mt-1`}>
            {Object.entries(PRIORITES).map(([cle, p]) => <option key={cle} value={cle}>{p.label}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Début
          <input type="date" value={valeurs.date_debut || ''} onChange={maj('date_debut')} className={`${champ} mt-1`} />
        </label>
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Échéance
          <input type="date" value={valeurs.echeance || ''} onChange={maj('echeance')} className={`${champ} mt-1`} />
        </label>
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Responsable
          <input value={valeurs.responsable} onChange={maj('responsable')} placeholder="Ex. trésorière" className={`${champ} mt-1`} />
        </label>
      </div>
      {!initial?.id && (
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Sous-domaine (facultatif)
          <input value={valeurs.sous_domaine_nom} onChange={maj('sous_domaine_nom')} className={`${champ} mt-1`} />
        </label>
      )}
      <label className="block text-xs font-semibold text-[var(--text-muted)]">Résultats et livrables attendus
        <textarea value={valeurs.indicateurs} onChange={maj('indicateurs')} rows={2} className={`${champ} mt-1 resize-y`} />
      </label>
      <label className="block text-xs font-semibold text-[var(--text-muted)]">Note de suivi
        <textarea value={valeurs.note_suivi} onChange={maj('note_suivi')} rows={2} placeholder="Où en est l'association, ce qui a été convenu…" className={`${champ} mt-1 resize-y`} />
      </label>
      <Erreur message={erreur} />
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onAnnuler} className={boutonSecondaire}><X className="w-4 h-4" />Annuler</button>
        <button type="submit" disabled={envoi} aria-busy={envoi || undefined} className={boutonPrincipal}>
          {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Enregistrer
        </button>
      </div>
    </form>
  );
}

/* Programmer le plan : le serveur propose des dates selon la priorité,
   puis chaque date s'ajuste à la main, ligne par ligne, avec la personne. */
function Programmation({ projetId, onFait, onAjouter, peutAjouter }) {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [depart, setDepart] = useState(aujourdhui);
  const [ecraser, setEcraser] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [retour, setRetour] = useState('');

  const lancer = async () => {
    setEnvoi(true);
    setRetour('');
    try {
      const r = await adminApi.programmerPlan(projetId, { depart, ecraser });
      if (r.datees) annoncerSucces(`${r.datees} action(s) datée(s) selon leur priorité.`);
      else setRetour('Toutes les actions ouvertes sont déjà datées. Cochez « Remplacer les dates déjà fixées » pour tout recalculer.');
      if (r.datees) onFait();
    } catch (e) {
      annoncerErreur(e.message || "Le calendrier n'a pas pu être programmé.");
    } finally {
      setEnvoi(false);
    }
  };

  const fenetres = Object.values(PRIORITES).map((p) => `${p.label.toLowerCase()}s ${p.fenetre}`).join(', ');
  return (
    <Carte>
      <h3 className="text-sm font-semibold text-[var(--text-bold)]">Programmer le plan avec le bénéficiaire</h3>
      <p className="text-xs text-[var(--text-muted)] mt-1 max-w-3xl">
        Calendrier proposé selon la priorité : {fenetres}. Ajustez ensuite chaque date (et, si besoin, la priorité)
        avec la personne.
      </p>
      <div className="flex flex-wrap items-end gap-3 mt-4">
        <label className="text-xs font-semibold text-[var(--text-muted)]">Démarrage
          <input type="date" value={depart} onChange={(e) => setDepart(e.target.value)} className={`${champ} mt-1`} />
        </label>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--text-main)] pb-3">
          <input type="checkbox" checked={ecraser} onChange={(e) => setEcraser(e.target.checked)} />
          Remplacer les dates déjà fixées
        </label>
        <div className="flex flex-wrap gap-2 ml-auto">
          <button type="button" onClick={onAjouter} disabled={!peutAjouter} className={boutonSecondaire}
            title={peutAjouter ? undefined : "Un diagnostic doit exister pour y rattacher l'action"}>
            <Plus className="w-4 h-4" />Ajouter une action
          </button>
          <button type="button" onClick={lancer} disabled={envoi || !depart} aria-busy={envoi || undefined} className={boutonPrincipal}>
            {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
            Proposer un calendrier
          </button>
        </div>
      </div>
      {retour && <p className="mt-2 text-xs font-semibold text-[var(--accent-texte)]" role="status">{retour}</p>}
    </Carte>
  );
}

const aujourdhuiIso = () => new Date().toISOString().slice(0, 10);
export const estEnRetard = (a) => Boolean(a.echeance) && a.statut !== 'termine' && a.echeance < aujourdhuiIso();

/* L'avancement compte le travail fait DANS les actions : une action
   terminée vaut 1, une action en cours vaut la part de ses livrables
   cochés. Les « actions réalisées » ne comptent que les terminées. */
export function statistiquesDuPlan(actions) {
  const total = actions.length;
  const part = (a) => {
    if (a.statut === 'termine') return 1;
    const l = a.livrables || [];
    return l.length ? l.filter((x) => x.coche).length / l.length : 0;
  };
  return {
    total,
    avancement: total ? Math.round((actions.reduce((s, a) => s + part(a), 0) / total) * 100) : 0,
    realisees: actions.filter((a) => a.statut === 'termine').length,
    datees: actions.filter((a) => a.date_debut && a.echeance).length,
    enRetard: actions.filter(estEnRetard).length,
  };
}

function Statistiques({ actions }) {
  const st = statistiquesDuPlan(actions);
  const genere = actions.map((a) => a.created_at).filter(Boolean).sort()[0];
  const cases = [
    { titre: 'Avancement', valeur: `${st.avancement} %`, barre: st.avancement },
    { titre: 'Actions réalisées', valeur: `${st.realisees}/${st.total}` },
    { titre: 'Actions datées', valeur: `${st.datees}/${st.total}` },
    { titre: 'En retard', valeur: st.enRetard, alerte: st.enRetard > 0 },
  ];
  return (
    <Carte>
      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {cases.map((c) => (
          <div key={c.titre}>
            <dt className="text-xs text-[var(--text-muted)]">{c.titre}</dt>
            <dd className={`text-2xl font-bold tabular-nums mt-1 ${c.alerte ? 'text-[var(--alerte)]' : 'text-[var(--text-bold)]'}`}>{c.valeur}</dd>
            {c.barre !== undefined && (
              <div className="mt-2 h-1.5 rounded-full bg-[var(--bg-main)] overflow-hidden" aria-hidden="true">
                <div className="h-full rounded-full bg-[var(--accent)] transition-all" style={{ width: `${c.barre}%` }} />
              </div>
            )}
          </div>
        ))}
      </dl>
      {genere && (
        <p className="text-xs text-[var(--text-muted)] mt-4">
          Plan généré le {fmtDate(genere.slice(0, 10))} à partir du diagnostic, textes issus du référentiel.
        </p>
      )}
    </Carte>
  );
}

/* La note de suivi s'écrit sur la ligne, sans ouvrir de formulaire. */
function NoteDeSuivi({ action, peutEcrire, enregistrer }) {
  const [texte, setTexte] = useState(action.note_suivi || '');
  const [envoi, setEnvoi] = useState(false);
  const modifie = texte !== (action.note_suivi || '');
  if (!peutEcrire && !action.note_suivi) return null;
  return (
    <details className="mt-2 group">
      <summary className="cursor-pointer text-xs font-semibold text-[var(--accent-texte)] select-none">
        Note de suivi{action.note_suivi ? '' : ' (vide)'}
      </summary>
      {peutEcrire ? (
        <div className="mt-2 space-y-2">
          <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={2}
            placeholder="Où en est l'association, ce qui a été convenu…" className={`${champ} resize-y`} />
          {modifie && (
            <button type="button" disabled={envoi} aria-busy={envoi || undefined} className={boutonSecondaire}
              onClick={async () => { setEnvoi(true); await enregistrer({ note_suivi: texte }, 'Note enregistrée.'); setEnvoi(false); }}>
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Enregistrer la note
            </button>
          )}
        </div>
      ) : (
        <TexteLignes texte={action.note_suivi} className="text-xs text-[var(--text-main)] mt-2 bg-[var(--bg-main)] rounded-pastille px-3 py-2" />
      )}
    </details>
  );
}

/* Les livrables : la preuve que l'action est faite. Le coach les coche ;
   quand tous le sont, le serveur passe l'action à « Terminé » (et la
   repasse « En cours » si l'un est décoché) — `livrables_plan.py`. */
function Livrables({ action, peutCocher, onChange }) {
  const [enCours, setEnCours] = useState('');
  const livrables = action.livrables || [];
  if (!livrables.length) return null;
  const faits = livrables.filter((l) => l.coche).length;
  const basculer = async (l) => {
    setEnCours(l.id);
    try {
      const r = await adminApi.cocherLivrable(action.id, l.id, !l.coche);
      if (r?.statut === 'termine' && action.statut !== 'termine') annoncerSucces('Tous les livrables sont cochés : action réalisée.');
      onChange();
    } catch (e) {
      annoncerErreur(e.message || "Le livrable n'a pas pu être enregistré.");
    } finally {
      setEnCours('');
    }
  };
  return (
    <div className="mt-3">
      <p className="text-[11px] font-semibold text-[var(--text-muted)] mb-1.5">
        Livrables · {faits}/{livrables.length} {peutCocher ? 'cochés' : 'constatés par le coach'}
      </p>
      <ul className="space-y-1.5">
        {livrables.map((l) => (
          <li key={l.id}>
            <label className={`flex items-start gap-2 text-xs ${peutCocher ? 'cursor-pointer' : ''}`}>
              {enCours === l.id
                ? <Loader2 className="w-4 h-4 mt-px shrink-0 animate-spin text-[var(--text-muted)]" />
                : <input type="checkbox" checked={l.coche} disabled={!peutCocher} onChange={() => basculer(l)}
                         className="w-4 h-4 mt-px shrink-0 accent-[var(--accent)]" />}
              <span className={l.coche ? 'text-[var(--text-bold)]' : 'text-[var(--text-main)]'}>
                {l.libelle}
                {l.coche && l.coche_le && (
                  <span className="text-[var(--text-muted)]"> · {fmtDate(l.coche_le.slice(0, 10))}{l.coche_par_nom ? `, ${l.coche_par_nom}` : ''}</span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Plan d'actions correctives. Sur chaque ligne, le coach ajuste sans
   ouvrir de formulaire la priorité, les dates, le statut et la note de
   suivi ; le crayon reste pour le texte et le responsable. */
export default function PlanActionsCoach({ projetId, diagnostics, actions, peutAgir, onChange }) {
  const [edition, setEdition] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [filtreDomaine, setFiltreDomaine] = useState('');
  const [filtrePriorite, setFiltrePriorite] = useState('');
  const domaines = useMemo(
    () => [...new Set(actions.map((a) => a.domaine_nom).filter(Boolean))].sort(),
    [actions]);
  const ordre = Object.keys(PRIORITES);

  const visibles = useMemo(() => actions
    .filter((a) => !filtreDomaine || a.domaine_nom === filtreDomaine)
    .filter((a) => !filtrePriorite || a.priorite === filtrePriorite)
    .slice()
    .sort((a, b) => (ordre.indexOf(a.priorite) - ordre.indexOf(b.priorite))
      || String(a.echeance || '9999').localeCompare(String(b.echeance || '9999'))),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [actions, filtreDomaine, filtrePriorite]);

  const enregistrer = async (action, champs, succes) => {
    try {
      await adminApi.updateAction(action.id, champs);
      if (succes) annoncerSucces(succes);
      onChange();
    } catch (e) { annoncerErreur(e.message || "Ce changement n'a pas pu être enregistré."); }
  };

  const changerDate = (a, cle) => (e) => {
    const valeur = e.target.value || null;
    const debut = cle === 'date_debut' ? valeur : a.date_debut;
    const fin = cle === 'echeance' ? valeur : a.echeance;
    if (debut && fin && fin < debut) { annoncerErreur("L'échéance précède la date de début."); return; }
    enregistrer(a, { [cle]: valeur });
  };

  return (
    <div className="space-y-4">
      {actions.length > 0 && <Statistiques actions={actions} />}

      {peutAgir && (
        <>
          <Programmation projetId={projetId} onFait={onChange} onAjouter={() => setAjout(true)}
            peutAjouter={diagnostics.length > 0} />
          {ajout && (
            <Carte>
              <FormulaireAction diagnostics={diagnostics} onAnnuler={() => setAjout(false)}
                onEnregistrer={async (d) => { await adminApi.createAction(d); setAjout(false); onChange(); }} />
            </Carte>
          )}
        </>
      )}

      {actions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrer par priorité">
          {[['', 'Toutes', actions.length], ...ordre.map((p) => [p, PRIORITES[p].label, actions.filter((a) => a.priorite === p).length])]
            .map(([cle, libelle, n]) => (
              <button key={cle || 'toutes'} type="button" aria-pressed={filtrePriorite === cle}
                onClick={() => setFiltrePriorite(cle)}
                className={`px-3.5 py-2 rounded-xl border text-[13px] font-semibold transition-all ${filtrePriorite === cle
                  ? 'bg-[var(--accent)] border-[var(--accent)] text-[#0A1128]'
                  : 'border-[var(--border)] text-[var(--text-main)] hover:bg-[var(--bg-main)]'}`}>
                {libelle} <span className="tabular-nums opacity-70">{n}</span>
              </button>
            ))}
          {domaines.length > 1 && (
            <select value={filtreDomaine} onChange={(e) => setFiltreDomaine(e.target.value)}
                    aria-label="Filtrer par domaine" className="filtre min-w-[12rem] ml-auto">
              <option value="">Tous les domaines</option>
              {domaines.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
        </div>
      )}

      {actions.length === 0 ? (
        <Vide icone={CheckCircle2} titre="Aucune action dans le plan"
          texte="Le plan se remplit tout seul à la clôture d'un diagnostic, avec ses actions correctives. Vous pouvez aussi créer une action à la main." />
      ) : visibles.length === 0 ? (
        <Vide icone={CheckCircle2} titre="Aucune action pour ces filtres" texte="Changez de domaine ou de priorité." />
      ) : (
        <ul className="space-y-3">
          {visibles.map((a) => (
            <li key={a.id} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-[var(--radius-card,16px)] p-4">
              {edition === a.id ? (
                <FormulaireAction initial={a} diagnostics={diagnostics} onAnnuler={() => setEdition(null)}
                  onEnregistrer={async (d) => {
                    const { id, diagnostic, created_at, updated_at, livrables, priorite_libelle, statut_libelle, ...champs } = d;
                    await adminApi.updateAction(a.id, champs);
                    setEdition(null);
                    onChange();
                  }} />
              ) : (
                <div className="flex flex-col xl:flex-row xl:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      {peutAgir ? (
                        <select value={a.priorite} aria-label="Priorité de l'action"
                          onChange={(e) => enregistrer(a, { priorite: e.target.value })}
                          className={`text-xs font-semibold rounded-lg border px-2 py-1 ${PRIORITES[a.priorite]?.className || ''}`}>
                          {ordre.map((p) => <option key={p} value={p}>{PRIORITES[p].label}</option>)}
                        </select>
                      ) : (
                        <Pastille className={PRIORITES[a.priorite]?.className}>{PRIORITES[a.priorite]?.label}</Pastille>
                      )}
                      {(a.domaine_nom || a.sous_domaine_nom) && (
                        <span className="text-xs text-[var(--text-muted)]">{[a.domaine_nom, a.sous_domaine_nom].filter(Boolean).join(' · ')}</span>
                      )}
                      {a.priorite_referentiel && a.priorite_referentiel !== a.priorite && (
                        <Pastille className="text-[var(--text-muted)] bg-[var(--bg-main)] border-[var(--border)]">référentiel : {PRIORITES[a.priorite_referentiel]?.label.toLowerCase()}</Pastille>
                      )}
                      {estEnRetard(a) && <Pastille className="text-[var(--alerte)] bg-[var(--alerte-fond)]">en retard</Pastille>}
                    </div>
                    <TexteLignes texte={a.description} className="text-sm font-semibold text-[var(--text-bold)]" />
                    {a.responsable && <p className="text-xs text-[var(--text-muted)] mt-1">Responsable : {a.responsable}</p>}
                    {a.livrables?.length
                      ? <Livrables action={a} peutCocher={peutAgir} onChange={onChange} />
                      : a.indicateurs && <TexteLignes texte={a.indicateurs} className="text-xs text-[var(--text-muted)] mt-1" />}
                    <NoteDeSuivi key={a.note_suivi} action={a} peutEcrire={peutAgir}
                      enregistrer={(champs, succes) => enregistrer(a, champs, succes)} />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-[repeat(3,minmax(0,11rem))_auto] gap-3 shrink-0 items-end">
                    <label className="text-[11px] font-semibold text-[var(--text-muted)]">Début
                      <input type="date" value={a.date_debut || ''} disabled={!peutAgir} onChange={changerDate(a, 'date_debut')}
                        className={`${champ} mt-1 py-2`} />
                    </label>
                    <label className="text-[11px] font-semibold text-[var(--text-muted)]">Échéance
                      <input type="date" value={a.echeance || ''} disabled={!peutAgir} onChange={changerDate(a, 'echeance')}
                        className={`${champ} mt-1 py-2 ${estEnRetard(a) ? 'border-[var(--alerte)]' : ''}`} />
                    </label>
                    <label className="text-[11px] font-semibold text-[var(--text-muted)]">Statut
                      <select value={a.statut} onChange={(e) => enregistrer(a, { statut: e.target.value })}
                        className={`${champ} mt-1 py-2 font-semibold`}>
                        {Object.entries(STATUTS_ACTION).map(([cle, libelle]) => (
                          // « Terminé » se déduit des livrables quand l'action en a.
                          <option key={cle} value={cle}
                                  disabled={cle === 'termine' && a.livrables?.length > 0 && a.livrables.some((l) => !l.coche)}>
                            {libelle}
                          </option>
                        ))}
                      </select>
                    </label>
                    {peutAgir && (
                      <div className="flex items-center gap-1 pb-1">
                        <button type="button" onClick={() => setEdition(a.id)} aria-label="Modifier le texte de l'action"
                          className="act act-icone act-tertiaire"><Pencil className="w-4 h-4" /></button>
                        <button type="button" aria-label="Supprimer l'action"
                          onClick={async () => (await confirmer('Retirer cette action du plan ?')) && enregistrerSuppression(a)}
                          className="act act-icone act-tertiaire"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  async function enregistrerSuppression(a) {
    try {
      await adminApi.deleteAction(a.id);
      annoncerSucces('Action retirée du plan.');
      onChange();
    } catch (e) { annoncerErreur(e.message || "L'action n'a pas pu être retirée."); }
  }
}
